import { Observable } from 'rxjs';
import _ from 'lodash';
import { startSubmit, stopSubmit, initialize, formValueSelector } from 'redux-form/immutable';
import { push, replace } from 'react-router-redux';
import { getParamByISO } from 'iso-country-currency';
import { ActionsObservable } from 'redux-observable';
import { Store } from 'redux';

import { selectUserLocations } from 'entities/user/selectors';
import LocationApi from 'entities/location/api';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import UserApi from 'entities/user/api';
import { geocodeByPlaceId } from 'utils/geocodeByPlaceId';

import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  removeSuccessAction,
  removeFailedAction,
  InitAction,
} from './actions';
import { FORM_IDENTIFIER, INIT, SAVE, RESOLVE_LOCATION_DETAILS, REMOVE } from './constants';

const init = (action$: ActionsObservable<InitAction>, store: Store) =>
  action$.ofType(INIT).mergeMap(({ id }) => {
    const actions = [];
    const state = store.getState();

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationApi.fetchAll());
    }

    if (id === 'new') {
      actions.push(Observable.of(initialize(FORM_IDENTIFIER, {})));
    } else {
      const location = locations.get(id);
      if (location) {
        const { coordinates, picture, ...commonProps } = location.toJS();
        actions.push(
          Observable.of(
            initialize(FORM_IDENTIFIER, {
              ...commonProps,
              latitude: coordinates[0],
              longitude: coordinates[1],
            })
          )
        );
      } else {
        actions.push(Observable.of(initFailedAction(), replace('/locations')));
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<InitAction>) =>
  action$.ofType(SAVE).mergeMap(({ data: { latitude, longitude, ...rest } }) => {
    const coordinates = [`${latitude}`, `${longitude}`];

    const payload = { ...rest, coordinates };

    return Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(rest.id ? LocationApi.update(rest.id, payload) : LocationApi.create(payload))
        .mergeMap((action) =>
          UserApi.fetch().mergeMap((fetchMeAction) =>
            [
              action,
              saveSuccessAction(action),
              stopSubmit(FORM_IDENTIFIER),
              showMessageAction(rest.id ? 'Changes Saved!' : 'Location Created!'),
              push('/locations'),
              fetchMeAction,
            ].filter(Boolean)
          )
        )
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    );
  });

const resolveLocationDetails = (action$: ActionsObservable<InitAction>, store: Store) =>
  action$.ofType(RESOLVE_LOCATION_DETAILS).mergeMap(({ place }) => {
    const actions = [];

    actions.push(
      Observable.from(geocodeByPlaceId(place.placeId)).concatMap((geocoderResult) => {
        const valueSelector = formValueSelector(FORM_IDENTIFIER);
        const state = store.getState();
        const formValues = valueSelector(
          state,
          'id',
          'name',
          'country',
          'countryCode',
          'state',
          'stateCode',
          'currencyCode',
          'region',
          'latitude',
          'longitude',
          'radius'
        );
        try {
          if (Array.isArray(geocoderResult) && geocoderResult.length > 0) {
            const [geoInfo] = geocoderResult;
            // resolving country and countryCode and stateCode and currencyCode:
            if (Array.isArray(geoInfo.address_components)) {
              geoInfo.address_components.find(
                ({ types, long_name, short_name }: Record<string, string>) => {
                  if (Array.isArray(types) && types.includes('country')) {
                    formValues.country = long_name || formValues.country;
                    formValues.countryCode = short_name || formValues.countryCode;
                    formValues.currencyCode = getParamByISO(formValues.countryCode, 'currency');
                    return true;
                  }
                  return false;
                }
              );
            }
            geoInfo.address_components.find(({ types, short_name, long_name }) => {
              if (Array.isArray(types) && types.includes('administrative_area_level_1')) {
                formValues.state = long_name || formValues.state;
                formValues.stateCode = short_name || formValues.stateCode;
                return true;
              }
              return false;
            });

            // resolving coordinates
            const { lat, lng } = geoInfo.geometry.location.toJSON();
            formValues.latitude = lat || formValues.latitude;
            formValues.longitude = lng || formValues.longitude;
            formValues.radius = formValues.radius || 10;
          }
        } catch (error) {
          console.warn({ error });
        }

        return Observable.of(initialize(FORM_IDENTIFIER, formValues));
      })
    );

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const remove = (action$) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(LocationApi.delete(payload.locationId))
      .mergeMap((action) =>
        UserApi.fetch().mergeMap((fetchMeAction) =>
          [
            action,
            removeSuccessAction(),
            push('/locations'),
            showMessageAction('Location Removed!'),
            fetchMeAction,
          ].filter(Boolean)
        )
      )
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction(
            e?.responseBody?.message ?? 'An error occurred during saving, please try again.'
          )
        )
      )
  );

export default [init, save, resolveLocationDetails, remove];
